import { RendererFilter, RendererFilterType } from '@report-content';

// Narrowing works on discrete value selections; range bounds can't be matched against a parent's value map.
export function supportsParentFilter(filterType?: RendererFilterType | null): boolean {
    return filterType == 'dropdown' || filterType == 'multiselect';
}

// Parent-filter narrowing and "Only display relevant values" are mutually exclusive; the latter wins.
export function resolveParentFilter(
    field: RendererFilter,
    fields: Array<RendererFilter>,
    onlyDisplayRelevantValues: boolean,
): RendererFilter | null {
    if (onlyDisplayRelevantValues || !field.parent_reference_name || !supportsParentFilter(field.filterType)) {
        return null;
    }
    const parentField = fields.find((f) => f.reference_name_escaped === field.parent_reference_name);
    if (!parentField || parentField.filter_column != 'Y' || !supportsParentFilter(parentField.filterType)) {
        return null;
    }
    return parentField;
}

// Returns the set of reference_name_escaped values that must not be selectable
// as a parent for currentField: itself, plus every filter that is currently its
// descendant in the parent chain (selecting a descendant would close a cycle).
function getExcludedParentReferenceNames(fields: Array<RendererFilter>, currentField: RendererFilter): Set<string> {
    const excluded = new Set<string>([currentField.reference_name_escaped]);

    const isDescendantOfCurrent = (field: RendererFilter): boolean => {
        const visited = new Set<string>();
        let ref = field.parent_reference_name;
        while (ref) {
            if (ref === currentField.reference_name_escaped) {
                return true;
            }
            if (visited.has(ref)) {
                // Already-broken chain in stored data; stop instead of looping forever.
                return false;
            }
            visited.add(ref);
            ref = fields.find((f) => f.reference_name_escaped === ref)?.parent_reference_name ?? null;
        }
        return false;
    };

    fields.forEach((field) => {
        if (field.reference_name_escaped !== currentField.reference_name_escaped && isDescendantOfCurrent(field)) {
            excluded.add(field.reference_name_escaped);
        }
    });

    return excluded;
}

export default function getAvailableParentFilters(
    fields: Array<RendererFilter>,
    currentField: RendererFilter,
): Array<RendererFilter> {
    const excluded = getExcludedParentReferenceNames(fields, currentField);
    return fields.filter((f) => !excluded.has(f.reference_name_escaped) && supportsParentFilter(f.filterType));
}